import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import './styles/visibility_graph.css';
import { getWeek } from '../../../components/performance-components/week_number.js';
import ForecastRelease from '../forecast_release.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  TimeScale
);

const VisibilityGraph = ({ airport }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forecastValue, setForecastValue] = useState(null);
  const [cqlFechaMin, setCqlFechaMin] = useState(null);
  const [cqlFechaMax, setCqlFechaMax] = useState(null);
  const [weekNumber, setWeekNumber] = useState(null);

  // Obtiene la fecha inicial con minutos y segundos en cero
  const getStartingDate = () => {
    const current = new Date();
    const adjustedDateTime = new Date(
      current.getFullYear(),
      current.getMonth(),
      current.getDate(),
      current.getHours(),
      0,
      0,
      0
    );
    return adjustedDateTime.toISOString();
  };

  // Llama a ForecastRelease al montar el componente
  useEffect(() => {
    const initialDate = getStartingDate();
    const dateObj = new Date(initialDate);
    dateObj.setDate(dateObj.getDate() - 1);
    const adjustedDate = dateObj.toISOString();
    ForecastRelease(adjustedDate, setForecastValue, 'VISIBILITY:visibility_live_demo');
  }, []);

  // Ajusta las fechas para que el eje X cubra 48 horas
  // cqlFechaMin se usa tal cual forecastValue y cqlFechaMax se obtiene sumándole 2 días.
  useEffect(() => {
    if (!forecastValue) return;
    const startDate = new Date(forecastValue);
    const endDate = new Date(forecastValue);
    endDate.setDate(startDate.getDate() + 2); // 48 horas de predicción
    setCqlFechaMin(startDate.toISOString());
    setCqlFechaMax(endDate.toISOString());
    setWeekNumber(getWeek(new Date(forecastValue)));
  }, [forecastValue]);

  // Función que convierte el valor de VISIBILITY_MATRIX_RISK a metros
  const convertRiskToMeters = (risk) => {
    const riskNumber = Number(risk);
    if (riskNumber === 4) return 350;
    if (riskNumber === 3) return 600;
    if (riskNumber === 2) return 1000;
    if (riskNumber === 1) return 3000;
    return null;
  };

  // Función que asigna un color fijo según el valor de VISIBILITY_MATRIX_RISK
  const convertRiskToColor = (risk) => {
    const riskNumber = Number(risk);
    if (riskNumber === 1) return 'green';
    if (riskNumber === 2) return 'blue';
    if (riskNumber === 3) return 'yellow';
    if (riskNumber === 4) return 'red';
    return '#000000';
  };

  // Llama a la API para obtener los datos, agruparlos en buckets de 30 minutos
  // y quedarse con el dato (convertido a metros y con su color) de peor visibilidad (valor mínimo) en cada bucket.
  useEffect(() => {
    if (!cqlFechaMin || !cqlFechaMax || !weekNumber) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/visibilityGraph?cqlFechaMax=${encodeURIComponent(
            cqlFechaMax
          )}&cqlFechaMin=${encodeURIComponent(cqlFechaMin)}&airport=${airport}&weekNumber=${encodeURIComponent(
            weekNumber
          )}`
        );
        if (!response.ok) throw new Error('Error fetching API data');
        const data = await response.json();

        // Agrupamos los registros en buckets de 30 minutos.
        // NOTA: No se modifica la fecha ya que la base de datos ya está en UTC.
        const groupedData = {};
        data.forEach(item => {
          const timestamp = new Date(item.VALID_TIMESTAMP); // Ya en UTC
          const minutes = timestamp.getUTCMinutes();
          const groupMinutes = minutes < 30 ? 0 : 30;
          const groupDate = new Date(Date.UTC(
            timestamp.getUTCFullYear(),
            timestamp.getUTCMonth(),
            timestamp.getUTCDate(),
            timestamp.getUTCHours(),
            groupMinutes,
            0,
            0
          ));
          const groupKey = groupDate.toISOString();
          const convertedValue = convertRiskToMeters(item.VISIBILITY_MATRIX_RISK);
          const color = convertRiskToColor(item.VISIBILITY_MATRIX_RISK);
          if (!groupedData[groupKey]) {
            groupedData[groupKey] = {
              date: groupDate,
              value: convertedValue,
              colour: color,
            };
          } else {
            if (convertedValue < groupedData[groupKey].value) {
              groupedData[groupKey] = {
                date: groupDate,
                value: convertedValue,
                colour: color,
              };
            }
          }
        });
        const sortedKeys = Object.keys(groupedData).sort();
        const labels = sortedKeys.map(key => groupedData[key].date);
        const visibilityValues = sortedKeys.map(key => groupedData[key].value);
        const colors = sortedKeys.map(key => groupedData[key].colour);

        setChartData({
          labels,
          datasets: [
            {
              label: 'Visibility level (m)',
              data: visibilityValues,
              fill: true,
              backgroundColor: 'rgba(75,192,192,0.2)',
              borderColor: 'rgba(75,192,192,1)',
              pointBackgroundColor: colors,
              pointBorderColor: 'rgba(255,255,255,1)',
              pointRadius: 4.5,
              borderWidth: 2,
              tension: 0.4,
            },
          ],
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cqlFechaMin, cqlFechaMax, weekNumber, airport]);

  if (loading) return <div className="spinner">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="visibility-graph-container">
      <Line
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { boxWidth: 15 } },
            title: {
              display: true,
              text: `Visibility Forecast for ${airport}`,
              font: { size: 18 },
            },
            tooltip: {
              callbacks: {
                // Override del title para que muestre la fecha en UTC sin conversión a local
                title: function(tooltipItems) {
                  const dt = new Date(tooltipItems[0].parsed.x);
                  const year = dt.getUTCFullYear();
                  const month = ('0' + (dt.getUTCMonth() + 1)).slice(-2);
                  const day = ('0' + dt.getUTCDate()).slice(-2);
                  const hours = ('0' + dt.getUTCHours()).slice(-2);
                  const minutes = ('0' + dt.getUTCMinutes()).slice(-2);
                  return `${year}-${month}-${day} ${hours}:${minutes}`;
                },
                // El label muestra el valor en metros
                label: (context) => `Visibility: ${context.raw} m`,
              },
            },
            zoom: {
              pan: { enabled: true, mode: 'x' },
              zoom: { wheel: { enabled: true }, mode: 'x' },
            },
          },
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'hour',
                stepSize: 1.5,
                displayFormats: {
                  hour: 'MMM dd, HH:mm',
                },
              },
              ticks: {
                autoSkip: false,
                maxRotation: 45,
                minRotation: 45,
                font: { size: 12 },
                // Forzamos el formato de los ticks en UTC
                callback: function(value) {
                  const date = new Date(value);
                  const year = date.getUTCFullYear();
                  const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
                  const day = ('0' + date.getUTCDate()).slice(-2);
                  const hours = ('0' + date.getUTCHours()).slice(-2);
                  const minutes = ('0' + date.getUTCMinutes()).slice(-2);
                  return `${year}-${month}-${day} ${hours}:${minutes}`;
                }
              }
            },
            y: {
              beginAtZero: true,
              max: 3500,
              ticks: { font: { size: 12 } },
            },
          },
        }}
      />
    </div>
  );
};

export default VisibilityGraph;
